import { useCallback } from 'react';
import { useIntl } from 'react-intl';
import {
  IssuedTicketFindResultDTO,
  SelfServiceTicketDTO,
} from '@/api/trafikportalen';
import {
  formatDateTimeReadable,
  formatRelativeDateTime,
  formatTimeLeft,
} from '@/helpers/formatting';
import { DateTime } from 'luxon';

export const TICKET_STATES = [
  'active',
  'inactive',
  'expired',
  'revoked',
  'cancelled',
  'blacklisted',
];

interface TicketStatusOption {
  label: string;
  description: string;
  ariaDescription?: string;
}

export const useTicketStatusOptions = () => {
  const { formatMessage } = useIntl();

  const getStatusTranslation = useCallback(
    ({ state }: { state: string }) => {
      switch (state) {
        case 'active':
          return formatMessage({ defaultMessage: 'Aktiv' });
        case 'inactive':
          return formatMessage({ defaultMessage: 'Oaktiverad' });
        case 'expired':
          return formatMessage({ defaultMessage: 'Utgången' });
        case 'rejected':
          return formatMessage({ defaultMessage: 'Avslagen' });
        case 'blacklisted':
          return formatMessage({ defaultMessage: 'Svartlistad' });
        // marked_for_deletion is an internal state and will result in revoked
        case 'marked_for_deletion':
        case 'revoked':
          return formatMessage({ defaultMessage: 'Avslutad' });
        case 'cancelled':
          return formatMessage({ defaultMessage: 'Avbruten' });
        default:
          return state;
      }
    },
    [formatMessage],
  );

  const getStatusTranslationLong = useCallback(
    (
      ticket: IssuedTicketFindResultDTO | SelfServiceTicketDTO,
      deadline = ticket.expires_at,
    ): TicketStatusOption => {
      if (ticket.state === 'active' && deadline) {
        return {
          label: formatMessage({
            defaultMessage: 'Aktiv till',
          }),
          description: `${formatRelativeDateTime(deadline)} (${formatTimeLeft(DateTime.fromISO(deadline), formatMessage)})`,
          ariaDescription: formatDateTimeReadable(deadline),
        };
      } else if (ticket.state === 'inactive' && deadline) {
        return {
          label: formatMessage({
            defaultMessage: 'Aktivera före',
          }),
          description: formatRelativeDateTime(deadline),
          ariaDescription: formatDateTimeReadable(deadline),
        };
      }
      if (ticket.state === 'marked_for_deletion') {
        return {
          label: formatMessage({
            defaultMessage: 'Avslutad',
          }),
          description: formatMessage({
            defaultMessage: '(upp till 5min fördröjning i bobcat)',
          }),
        };
      }

      return {
        label: getStatusTranslation(ticket),
        description: '',
      };
    },
    [formatMessage, getStatusTranslation],
  );

  return {
    getStatusTranslationLong,
    getStatusTranslation,
  };
};
